import { useState, useEffect } from 'react'
import { DetalhesAssinaturaModal } from '../components/DetalhesAssinaturaModal'
import { EditarAssinaturaModal } from '../components/EditarAssinaturaModal'
import { SuccessModal } from '../components/SuccessModal'
import { ErrorModal } from '../components/ErrorModal'
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabase = createClient(supabaseUrl, supabaseKey)

import {
    Users,
    CreditCard,
    AlertTriangle,
    DollarSign,
    Search,
    Filter,
    Eye,
    XCircle,
    CheckCircle,
    Clock,
    RefreshCw,
    Edit,
    Trash2,
    Pause
} from 'lucide-react'

interface Assinatura {
    id: string
    user_id: string
    corretor_nome: string
    corretor_email: string
    plano_id: string
    plano_nome: string
    plano_preco: number
    status: string
    data_inicio: string
    data_fim: string | null
    proximo_vencimento: string | null
    recorrencia: {
        id: string
        id_rec_efi: string
        status: string
        periodicidade: string
        detalhes_efi?: {
            cancelamento_origem?: string
            motivo_cancelamento?: string
        } | null
    } | null
    asaas_assinatura: {
        id: string
        asaas_subscription_id: string
        status: string
        cycle: string
        billing_type: string
        valor: number
    } | null
    total_pagamentos: number
    pagamentos_confirmados: number
    created_at: string
    ciclo_pagamento?: string
    metodo_pagamento?: string
    observacoes?: string | null
}

interface Estatisticas {
    total_ativas: number
    total_trial: number
    total_vencidas: number
    receita_mensal_esperada: number
    receita_recebida_mes_atual: number
    pagamentos_pendentes: number
    recorrencias_ativas: number
}

interface Plano {
    id: string
    nome: string
    nome_display: string
    preco_mensal: number
}

export function ControleAssinaturas() {
    const [assinaturas, setAssinaturas] = useState<Assinatura[]>([])
    const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null)
    const [planos, setPlanos] = useState<Plano[]>([])
    const [loading, setLoading] = useState(true)
    const [filtroStatus, setFiltroStatus] = useState<string>('')
    const [filtroPlan, setFiltroPlan] = useState<string>('')
    const [busca, setBusca] = useState('')
    const [paginaAtual, setPaginaAtual] = useState(0)
    const [totalPaginas, setTotalPaginas] = useState(0)
    const [modalAberto, setModalAberto] = useState(false)
    const [modalEditarAberto, setModalEditarAberto] = useState(false)
    const [assinaturaSelecionada, setAssinaturaSelecionada] = useState<Assinatura | null>(null)
    const [assinaturaParaEditar, setAssinaturaParaEditar] = useState<string | null>(null)
    const [processando, setProcessando] = useState(false)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [showErrorModal, setShowErrorModal] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const itensPorPagina = 20

    useEffect(() => {
        carregarPlanos()
    }, [])

    useEffect(() => {
        carregarDados()
    }, [filtroStatus, filtroPlan, busca, paginaAtual])

    const carregarPlanos = async () => {
        try {
            const { data, error } = await supabase
                .from('planos')
                .select('id, nome, nome_display, preco_mensal')
                .eq('status', 'ativo')
                .order('ordem_exibicao')

            if (error) throw error
            if (data) {
                setPlanos(data)
            }
        } catch (error) {
            console.error('Erro ao carregar planos:', error)
        }
    }

    const carregarDados = async () => {
        setLoading(true)
        try {
            // Carregar estatísticas
            const { data: statsData } = await supabase.rpc('obter_estatisticas_assinaturas')
            if (statsData?.sucesso) {
                setEstatisticas(statsData.estatisticas)
            }

            // Carregar assinaturas
            const { data: assinaturasData, error: assinaturasError } = await supabase.rpc('listar_todas_assinaturas_ativas', {
                p_filtro_status: filtroStatus && filtroStatus !== 'todos' ? filtroStatus : null,
                p_filtro_plano: filtroPlan && filtroPlan !== 'todos' ? filtroPlan : null,
                p_busca: busca && busca.trim().length > 0 ? busca.trim() : null,
                p_limit: itensPorPagina,
                p_offset: paginaAtual * itensPorPagina
            })

            if (assinaturasError) throw assinaturasError

            if (assinaturasData?.sucesso) {
                setAssinaturas(assinaturasData.assinaturas || [])
                setTotalPaginas(Math.ceil((assinaturasData.total || 0) / itensPorPagina))
            }
        } catch (error) {
            console.error('Erro ao carregar dados:', error)
        } finally {
            setLoading(false)
        }
    }

    // Determinar o status a ser exibido considerando a recorrência PIX
    const getStatusExibicao = (assinatura: Assinatura): string => {
        if (assinatura.recorrencia) {
            const stRec = String(assinatura.recorrencia.status || '').toLowerCase()
            if (['cancelada', 'rejeitada', 'expirada'].includes(stRec)) {
                return 'cancelada'
            }
            if (['aprovada', 'ativa', 'criada'].includes(stRec)) {
                return 'ativa'
            }
        }
        return assinatura.status
    }

    const isCanceladoViaAppBanco = (assinatura: Assinatura): boolean => {
        const obs = String(assinatura.observacoes || '').toLowerCase()
        if (obs.includes('cancelado via app do banco')) return true

        const motivo = String(assinatura.recorrencia?.detalhes_efi?.motivo_cancelamento || '').toLowerCase()
        if (motivo.includes('cancelado via app do banco')) return true

        const origem = String(assinatura.recorrencia?.detalhes_efi?.cancelamento_origem || '')
        if (origem === 'app_banco') return true

        const stRec = String(assinatura.recorrencia?.status || '').toLowerCase()
        return ['cancelada', 'rejeitada'].includes(stRec) && assinatura.metodo_pagamento === 'pix'
    }

    const getStatusBadge = (status: string) => {
        const badges = {
            ativa: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle, label: 'Ativa' },
            trial: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: Clock, label: 'Trial' },
            cancelada: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle, label: 'Cancelada' },
            expirada: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', icon: AlertTriangle, label: 'Expirada' },
            suspensa: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Pause, label: 'Suspensa' }
        }
        const badge = badges[status as keyof typeof badges] || badges.ativa
        const Icon = badge.icon
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                <Icon className="w-3 h-3" />
                {badge.label}
            </span>
        )
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value)
    }

    // Função helper para extrair apenas a data (YYYY-MM-DD) sem considerar fuso horário
    const extractDateOnly = (dateString: string | null): string | null => {
        if (!dateString) return null
        // Se já está no formato YYYY-MM-DD, retorna direto
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString
        }
        // Se está no formato ISO (YYYY-MM-DDTHH:mm:ss...), extrai apenas a parte da data
        const dateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/)
        return dateMatch ? dateMatch[1] : null
    }

    const formatDate = (date: string | null) => {
        if (!date) return '-'
        const dateOnly = extractDateOnly(date)
        if (!dateOnly) return '-'
        // Converte YYYY-MM-DD para DD/MM/YYYY
        const [year, month, day] = dateOnly.split('-')
        return `${day}/${month}/${year}`
    }

    const handleEditar = (assinaturaId: string) => {
        setAssinaturaParaEditar(assinaturaId)
        setModalEditarAberto(true)
    }

    const handleDeletar = async (assinaturaId: string) => {
        if (!confirm('Tem certeza que deseja deletar esta assinatura? Esta ação não pode ser desfeita.')) {
            return
        }

        setProcessando(true)
        try {
            const { data, error } = await supabase.rpc('deletar_assinatura', {
                p_assinatura_id: assinaturaId,
                p_motivo: 'Exclusão administrativa'
            })

            if (error) throw error

            if (data?.sucesso) {
                setSuccessMessage('Assinatura deletada com sucesso!')
                setShowSuccessModal(true)
                carregarDados()
            } else {
                setErrorMessage(data?.mensagem || 'Erro ao deletar assinatura')
                setShowErrorModal(true)
            }
        } catch (error: any) {
            console.error('Erro ao deletar assinatura:', error)
            setErrorMessage(error.message || 'Erro ao deletar assinatura')
            setShowErrorModal(true)
        } finally {
            setProcessando(false)
        }
    }

    const handleEditarSuccess = () => {
        carregarDados()
    }

    return (
        <div className="w-full">
            <div className="w-full space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Controle de Assinaturas</h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">Gerencie todas as assinaturas e pagamentos</p>
                    </div>
                    <button
                        onClick={carregarDados}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Atualizar
                    </button>
                </div>

                {/* Cards de Estatísticas */}
                {estatisticas && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Assinaturas Ativas</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                                        {estatisticas.total_ativas}
                                    </p>
                                </div>
                                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                                    <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Em Trial</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                                        {estatisticas.total_trial}
                                    </p>
                                </div>
                                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                                    <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Vencidas</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                                        {estatisticas.total_vencidas}
                                    </p>
                                </div>
                                <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-full">
                                    <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Receita Mensal</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                                        {formatCurrency(estatisticas.receita_mensal_esperada)}
                                    </p>
                                </div>
                                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                                    <DollarSign className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filtros e Busca */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Buscar por nome ou email..."
                                value={busca}
                                onChange={(e) => {
                                    setBusca(e.target.value)
                                    setPaginaAtual(0)
                                }}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                            />
                        </div>

                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                            <select
                                value={filtroStatus}
                                onChange={(e) => {
                                    setFiltroStatus(e.target.value)
                                    setPaginaAtual(0)
                                }}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="">Todos os Status</option>
                                <option value="ativa">Ativas</option>
                                <option value="trial">Trial</option>
                                <option value="cancelada">Canceladas</option>
                                <option value="expirada">Expiradas</option>
                                <option value="suspensa">Suspensas</option>
                            </select>
                        </div>

                        <div className="relative">
                            <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                            <select
                                value={filtroPlan}
                                onChange={(e) => {
                                    setFiltroPlan(e.target.value)
                                    setPaginaAtual(0)
                                }}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="">Todos os Planos</option>
                                {planos.map((plano) => (
                                    <option key={plano.id} value={plano.id}>
                                        {plano.nome_display} - {formatCurrency(plano.preco_mensal)}/mês
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Tabela de Assinaturas */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <RefreshCw className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
                        </div>
                    ) : assinaturas.length === 0 ? (
                        <div className="text-center py-12">
                            <Users className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                            <p className="text-gray-600 dark:text-gray-400">Nenhuma assinatura encontrada</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Corretor
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Plano
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Próx. Vencimento
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Pagamentos
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Recorrência
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Ações
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {assinaturas.map((assinatura) => (
                                            <tr key={assinatura.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                            {assinatura.corretor_nome}
                                                        </div>
                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                            {assinatura.corretor_email}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 dark:text-gray-100">{assinatura.plano_nome}</div>
                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                        {formatCurrency(assinatura.plano_preco)}/mês
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex flex-col gap-1 items-start">
                                                        {getStatusBadge(getStatusExibicao(assinatura))}
                                                        {isCanceladoViaAppBanco(assinatura) && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                                                                Cancelado via app do banco
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                    {formatDate(assinatura.proximo_vencimento)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 dark:text-gray-100">
                                                        {assinatura.pagamentos_confirmados}/{assinatura.total_pagamentos}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">confirmados</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {assinatura.recorrencia ? (
                                                        <div className="text-xs">
                                                            <div className="font-medium text-gray-900 dark:text-gray-100">
                                                                {assinatura.recorrencia.status}
                                                            </div>
                                                            <div className="text-gray-500 dark:text-gray-400">
                                                                {assinatura.recorrencia.periodicidade}
                                                            </div>
                                                        </div>
                                                    ) : assinatura.asaas_assinatura ? (
                                                        <div className="text-xs">
                                                            <div className="font-medium text-gray-900 dark:text-gray-100">
                                                                {assinatura.asaas_assinatura.status === 'ACTIVE' ? 'criada' : assinatura.asaas_assinatura.status.toLowerCase()}
                                                            </div>
                                                            <div className="text-gray-500 dark:text-gray-400">
                                                                {assinatura.asaas_assinatura.cycle === 'MONTHLY' ? 'MENSAL' : assinatura.asaas_assinatura.cycle}
                                                            </div>
                                                        </div>
                                                    ) : assinatura.ciclo_pagamento ? (
                                                        <div className="text-xs">
                                                            <div className="font-medium text-gray-900 dark:text-gray-100 uppercase">
                                                                {assinatura.ciclo_pagamento}
                                                            </div>
                                                            {assinatura.metodo_pagamento && (
                                                                <div className="text-gray-500 dark:text-gray-400 uppercase">
                                                                    {assinatura.metodo_pagamento}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400 dark:text-gray-500">-</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => {
                                                                setAssinaturaSelecionada(assinatura)
                                                                setModalAberto(true)
                                                            }}
                                                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                                            title="Ver detalhes"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleEditar(assinatura.id)}
                                                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                                                            title="Editar assinatura"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeletar(assinatura.id)}
                                                            disabled={processando}
                                                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                            title="Deletar assinatura"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Paginação */}
                            {totalPaginas > 1 && (
                                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                        Página {paginaAtual + 1} de {totalPaginas}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setPaginaAtual(Math.max(0, paginaAtual - 1))}
                                            disabled={paginaAtual === 0}
                                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-800"
                                        >
                                            Anterior
                                        </button>
                                        <button
                                            onClick={() => setPaginaAtual(Math.min(totalPaginas - 1, paginaAtual + 1))}
                                            disabled={paginaAtual >= totalPaginas - 1}
                                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-800"
                                        >
                                            Próxima
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Modal de Detalhes */}
                {assinaturaSelecionada && (
                    <DetalhesAssinaturaModal
                        isOpen={modalAberto}
                        onClose={() => {
                            setModalAberto(false)
                            setAssinaturaSelecionada(null)
                            carregarDados() // Recarregar dados ao fechar
                        }}
                        assinaturaId={assinaturaSelecionada.id}
                        userId={assinaturaSelecionada.user_id}
                        corretorNome={assinaturaSelecionada.corretor_nome}
                        corretorEmail={assinaturaSelecionada.corretor_email}
                        planoNome={assinaturaSelecionada.plano_nome}
                        planoPreco={assinaturaSelecionada.plano_preco}
                        status={getStatusExibicao(assinaturaSelecionada)}
                        dataInicio={assinaturaSelecionada.data_inicio}
                        dataFim={assinaturaSelecionada.data_fim}
                        dataProximoPagamento={assinaturaSelecionada.proximo_vencimento}
                        asaasAssinatura={assinaturaSelecionada.asaas_assinatura}
                        recorrencia={assinaturaSelecionada.recorrencia}
                        observacoes={assinaturaSelecionada.observacoes}
                    />
                )}

                {/* Modal de Edição */}
                {assinaturaParaEditar && (
                    <EditarAssinaturaModal
                        isOpen={modalEditarAberto}
                        onClose={() => {
                            setModalEditarAberto(false)
                            setAssinaturaParaEditar(null)
                        }}
                        assinaturaId={assinaturaParaEditar}
                        onSuccess={handleEditarSuccess}
                    />
                )}

                {/* Modal de Sucesso */}
                <SuccessModal
                    isOpen={showSuccessModal}
                    onClose={() => setShowSuccessModal(false)}
                    title="Sucesso!"
                    message={successMessage}
                    autoClose={true}
                    autoCloseDelay={2000}
                />

                {/* Modal de Erro */}
                <ErrorModal
                    isOpen={showErrorModal}
                    onClose={() => setShowErrorModal(false)}
                    title="Erro!"
                    message={errorMessage}
                    autoClose={false}
                />
            </div>
        </div>
    )
}

